import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { DateField } from "@/components/DateField";
import { api, errMsg } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { Loader, PageTitle } from "@/components/common";
import { download } from "@/pages/member/OperationDetail";

const iso = (d) => d.toISOString().slice(0, 10);

const Kpi = ({ label, value, sub, testId }) => (
  <div className="mr-card p-4" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-mr-muted">{label}</p>
    <p className="font-display font-bold text-2xl tabular mt-1">{value}</p>
    {sub && <p className="text-xs text-mr-text2 mt-1">{sub}</p>}
  </div>
);

function Weekly({ weeks }) {
  const max = Math.max(1, ...weeks.map((w) => w.active));
  if (!weeks.length) return <p className="text-sm text-mr-muted">Aucune activité sur la période.</p>;
  return (
    <div className="flex items-end gap-1 h-32" data-testid="engagement-weekly-chart">
      {weeks.map((w) => (
        <div key={w.week} className="flex-1 min-w-[14px] flex flex-col items-center gap-1" title={`${w.week} : ${w.active} membres actifs`}>
          <span className="text-[10px] tabular text-mr-muted">{w.active}</span>
          <div className="w-full bg-mr-accent rounded-t-[2px]" style={{ height: `${(w.active / max) * 90}px` }} />
          <span className="text-[9px] text-mr-muted">{w.week.split("-")[1]}</span>
        </div>
      ))}
    </div>
  );
}

function MemberTable({ rows }) {
  return (
    <div className="mr-card p-0 overflow-x-auto" data-testid="engagement-members-table">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-mr-muted border-b border-mr-border">
          {["Membre", "Ville", "Op. rejointes", "Op. portées", "Messages", "Publications", "Mises en relation (acceptées)", "Événements", "Voyages"].map((h) => <th key={h} className="p-2 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-b border-mr-border tabular" data-testid="engagement-member-row">
            <td className="p-2 font-medium whitespace-nowrap">{r.name}</td><td className="p-2">{r.city}</td><td className="p-2">{r.operations}</td><td className="p-2">{r.operations_led}</td>
            <td className="p-2">{r.messages}</td><td className="p-2">{r.posts}</td><td className="p-2">{r.intros_made} ({r.intros_accepted})</td><td className="p-2">{r.events}</td><td className="p-2">{r.trips}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

export default function Engagement() {
  const { settings } = useConfig();
  const [f, setF] = useState({ start: iso(new Date(Date.now() - 90 * 864e5)), end: iso(new Date()), sector: "", city: "" });
  const [d, setD] = useState(null);
  const qs = () => new URLSearchParams(f).toString();
  const load = () => api.get(`/admin/engagement?${qs()}`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="mr-fade-up space-y-6">
      <PageTitle eyebrow="Pilotage" title="Engagement du réseau">
        <button data-testid="engagement-export-button" className="mr-btn-secondary" onClick={() => download(`/admin/engagement?${qs()}&export=csv`, `engagement-${f.start}-${f.end}.csv`).catch((e) => toast.error(errMsg(e)))}><Download size={15} /> Exporter (CSV)</button>
      </PageTitle>
      <div className="mr-card grid sm:grid-cols-5 gap-3 items-end" data-testid="engagement-filters">
        <div><span className="mr-label">Du</span><DateField testId="engagement-start" value={f.start} onChange={(v) => setF({ ...f, start: v })} /></div>
        <div><span className="mr-label">Au</span><DateField testId="engagement-end" value={f.end} onChange={(v) => setF({ ...f, end: v })} /></div>
        <label><span className="mr-label">Secteur</span><select data-testid="engagement-sector" className="mr-input" value={f.sector} onChange={(e) => setF({ ...f, sector: e.target.value })}>
          <option value="">Tous</option>{(settings["catalog.sectors"] || []).map((s) => <option key={s}>{s}</option>)}</select></label>
        <label><span className="mr-label">Ville / commune</span><select data-testid="engagement-city" className="mr-input" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })}>
          <option value="">Toutes</option>{(settings["catalog.cities"] || []).map((s) => <option key={s}>{s}</option>)}</select></label>
        <button data-testid="engagement-apply" className="mr-btn-primary" onClick={load}>Appliquer</button>
      </div>
      {!d ? <Loader /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi testId="kpi-introductions" label="Mises en relation" value={`${d.introductions.accepted} / ${d.introductions.made}`} sub={`acceptées / faites · ${d.introductions.rate} %`} />
            <Kpi testId="kpi-operations" label="Opérations par membre" value={d.operations.per_member} sub={`${d.operations.commitments} adhésions · ${d.operations.members_in_operation} membres · ${d.operations.led} portées`} />
            <Kpi testId="kpi-weekly-active" label="Actifs par semaine" value={d.weekly_active_avg} sub={`moyenne sur ${d.members} membres du filtre`} />
            <Kpi testId="kpi-messages-posts" label="Messages · publications" value={`${d.messages} · ${d.posts}`} />
            <Kpi testId="kpi-events" label="Billets d'événements" value={d.events} />
            <Kpi testId="kpi-trips" label="Places de voyage" value={d.trips} />
            <Kpi testId="kpi-searches" label="« Je cherche » convertis" value={`${d.searches.rate} %`} sub={`${d.searches.posts} publiés · ${d.searches.answered} avec réponse · ${d.searches.contacted} mis en contact`} />
          </div>
          <div className="mr-card space-y-2"><p className="mr-eyebrow">Membres actifs par semaine</p><Weekly weeks={d.weekly_active} />
            <p className="text-xs text-mr-muted">Un membre est actif dans la semaine s'il a envoyé un message, publié, commenté ou utilisé l'application.</p></div>
          <MemberTable rows={d.rows} />
        </>
      )}
    </div>
  );
}
